import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Modal from '../components/Modal';
import NationalityDisplay from '../components/NationalityDisplay';
import { handlePhoneInput, cleanPhoneNumber } from '../utils/phoneValidation';
import NationalitySelect from '../components/NationalitySelect';
import './Users.css';

// Mapeo de nombres de roles a etiquetas
const ROLE_LABELS = {
  'superadmin': 'Super Administrador',
  'owner': 'Dueño del Negocio',
  'admin': 'Administrador',
  'teacher': 'Profesor',
  'secretary': 'Secretario',
  'student': 'Estudiante',
  'provider': 'Proveedor publicitario',
};

const Users = () => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');

  const { data: tenants } = useQuery(
    'tenants',
    async () => {
      const response = await api.get('/tenants');
      return response.data;
    },
    { enabled: true }
  );

  const { data: availableRoles } = useQuery(
    'roles',
    async () => {
      const response = await api.get('/users/roles');
      return response.data;
    },
    {
      onError: (err) => {
        // Error silencioso - los roles se manejan por permisos
      },
    }
  );

  const { data: users, isLoading, error } = useQuery(
    ['users', roleFilter],
    async () => {
      const params = roleFilter ? { role: roleFilter } : {};
      const response = await api.get('/users', { params });
      return response.data;
    },
    {
      retry: 1,
      onError: (err) => {
        if (err.code === 'ECONNABORTED' || err.message === 'Network Error') {
          toast.error('No se pudo conectar con el servidor. Verifica que el backend esté corriendo.');
        } else if (err.response?.status === 401) {
          toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
        } else {
          toast.error(err.response?.data?.detail || 'Error al cargar usuarios');
        }
      },
    }
  );

  const createUserMutation = useMutation(
    (data) => {
      return api.post('/users', data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success('Usuario creado exitosamente!');
        setIsModalOpen(false);
        setEditingUser(null);
      },
      onError: (err) => {
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail)) {
          const errorMessages = errorDetail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
          toast.error(`Error de validación: ${errorMessages}`);
        } else {
          toast.error(errorDetail || 'Error al crear usuario');
        }
      },
    }
  );

  const updateUserMutation = useMutation(
    ({ id, data }) => {
      return api.put(`/users/${id}`, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success('Usuario actualizado exitosamente!');
        setIsModalOpen(false);
        setEditingUser(null);
      },
      onError: (err) => {
        toast.error(err.response?.data?.detail || 'Error al actualizar usuario');
      },
    }
  );

  const toggleUserStatusMutation = useMutation(
    ({ id, is_active }) => {
      return api.put(`/users/${id}`, { is_active });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success('Estado del usuario actualizado!');
      },
      onError: (err) => {
        toast.error(err.response?.data?.detail || 'Error al cambiar estado');
      },
    }
  );

  const handleCreateClick = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  // Función para verificar si hay datos en el formulario antes de cerrar
  const handleModalClose = () => {
    // Esta función se llamará desde el Modal cuando se intente cerrar
    // El formulario manejará la confirmación internamente
    setIsModalOpen(false);
    setEditingUser(null);
  };

  const handleToggleStatus = (user) => {
    toggleUserStatusMutation.mutate({ id: user.id, is_active: !user.is_active });
  };

  const handleSubmit = async (formData) => {
    const { subscription_plan, ...userData } = formData;
    if (isPlatformAdmin && subscription_plan && userData.tenant_id) {
      try {
        await api.put(`/tenants/${userData.tenant_id}`, { subscription_plan });
        queryClient.invalidateQueries('tenants');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'No se pudo guardar el plan de suscripción');
        return;
      }
    }
    if (editingUser) {
      updateUserMutation.mutate({ id: editingUser.id, data: userData });
    } else {
      createUserMutation.mutate(userData);
    }
  };

  const getRoleLabel = (roleName) => {
    return ROLE_LABELS[roleName] || roleName;
  };

  const getTenantName = (tenantId) => {
    if (!tenantId || !tenants) return 'Sin organización';
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant ? tenant.name : `Organización #${tenantId}`;
  };

  const getTenantPlan = (tenantId) => {
    if (!tenantId || !tenants) return null;
    const tenant = tenants.find((item) => item.id === tenantId);
    return tenant?.subscription_plan || (tenant ? 'premium' : null);
  };

  const isPlatformAdmin = Boolean(
    currentUser?.is_superuser || currentUser?.roles?.some((role) => role.name === 'superadmin')
  );

  // Verificar si el usuario actual puede crear usuarios
  const canCreateUsers = () => {
    if (!currentUser) return false;
    const userRoles = currentUser.roles?.map(r => r.name) || [];
    const isSuperuser = currentUser.is_superuser || userRoles.includes('superadmin');
    
    // Superadmin, Owner y Secretary pueden crear usuarios
    return isSuperuser || userRoles.includes('owner') || userRoles.includes('secretary');
  };

  const filteredUsers = users?.filter(user => {
    if (planFilter && getTenantPlan(user.tenant_id) !== planFilter) return false;
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      user.full_name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.username?.toLowerCase().includes(searchLower)
    );
  }) || [];

  if (isLoading) {
    return <div className="loading">Cargando usuarios...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error">
          <h3>Error al cargar usuarios</h3>
          <p>{error.response?.data?.detail || error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="users-page">
      <div className="page-header">
        <div>
          <h1>Gestión de Usuarios</h1>
          <p>Administra los usuarios del sistema</p>
        </div>
        {canCreateUsers() && (
          <button className="btn btn-primary" onClick={handleCreateClick}>
            + Nuevo Usuario
          </button>
        )}
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="Buscar por nombre, email o usuario..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">Todos los roles</option>
          {availableRoles?.map(role => (
            <option key={role.id} value={role.name}>
              {ROLE_LABELS[role.name] || role.name}
            </option>
          ))}
        </select>
        {isPlatformAdmin && (
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="filter-select"
            aria-label="Plan de suscripción"
          >
            <option value="">Todas las membresías</option>
            <option value="free">Free</option>
            <option value="premium">Premium</option>
          </select>
        )}
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Email</th>
              <th>Usuario</th>
              <th>Nacionalidad</th>
              <th>Roles</th>
              <th>Organización</th>
              {isPlatformAdmin && <th>Plan de suscripción</th>}
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={isPlatformAdmin ? 10 : 9} className="empty-state">
                  <p>No hay usuarios registrados</p>
                  {canCreateUsers() && (
                    <button className="btn btn-primary" onClick={handleCreateClick}>
                      Crear Primer Usuario
                    </button>
                  )}
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id} className={!user.is_active ? 'inactive' : ''}>
                  <td>{user.id}</td>
                  <td>
                    <div className="user-info">
                      <strong>{user.full_name}</strong>
                      {user.is_superuser && (
                        <span className="superuser-badge">Superusuario</span>
                      )}
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>@{user.username}</td>
                  <td><NationalityDisplay code={user.nationality_code} /></td>
                  <td>
                    <div className="roles-list">
                      {user.roles && user.roles.length > 0 ? (
                        user.roles.map((role, idx) => (
                          <span key={role.id || idx} className="role-badge">
                            {getRoleLabel(role.name)}
                          </span>
                        ))
                      ) : (
                        <span className="no-roles">Sin roles</span>
                      )}
                    </div>
                  </td>
                  <td>{getTenantName(user.tenant_id)}</td>
                  {isPlatformAdmin && (
                    <td>
                      {getTenantPlan(user.tenant_id) ? (
                        <span className={`plan-badge plan-${getTenantPlan(user.tenant_id)}`}>
                          {getTenantPlan(user.tenant_id) === 'premium' ? 'PREMIUM' : 'FREE'}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  )}
                  <td>
                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEditClick(user)}
                        title="Editar"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => handleToggleStatus(user)}
                        title={user.is_active ? 'Desactivar' : 'Activar'}
                      >
                        {user.is_active ? '⏸️' : '▶️'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {isPlatformAdmin && (
        <p className="plan-hint">La membresía define las funciones disponibles para cada organización.</p>
      )}

      <Modal
        isOpen={isModalOpen}
        title={editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
        onClose={handleModalClose}
        closeOnOverlayClick={false}
      >
          <UserForm
            user={editingUser}
            currentUser={currentUser}
            tenants={tenants || []}
            availableRoles={availableRoles || []}
            onSubmit={handleSubmit}
            onCancel={handleModalClose}
            isLoading={createUserMutation.isLoading || updateUserMutation.isLoading}
          />
      </Modal>
    </div>
  );
};

const UserForm = ({ user, currentUser, tenants, availableRoles, onSubmit, onCancel, isLoading }) => {
  // Determinar qué roles puede crear el usuario actual (memoizado)
  const allowedRoles = useMemo(() => {
    if (!currentUser || !availableRoles) return [];
    
    const userRoles = currentUser.roles?.map(r => r.name) || [];
    const isSuperuser = currentUser.is_superuser || userRoles.includes('superadmin');
    
    // Superadmin puede crear dueños del negocio o proveedores publicitarios
    if (isSuperuser) {
      return availableRoles.filter(role => role.name === 'owner' || role.name === 'provider');
    }
    
    // Dueño del Negocio puede crear Profesores y Secretarios
    if (userRoles.includes('owner')) {
      return availableRoles.filter(role => 
        role.name === 'teacher' || role.name === 'secretary'
      );
    }
    
    // Secretario solo puede crear Estudiantes
    if (userRoles.includes('secretary')) {
      return availableRoles.filter(role => role.name === 'student');
    }
    
    // Por defecto, no puede crear ningún rol
    return [];
  }, [currentUser, availableRoles]);

  // Usar useRef para rastrear el usuario anterior y evitar reseteos innecesarios
  const previousUserRef = useRef(user);
  // Rastrear si ya se auto-seleccionó el rol para evitar re-seleccionarlo si el usuario lo deselecciona
  const autoSelectedRoleRef = useRef(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showCloseConfirmModal, setShowCloseConfirmModal] = useState(false);
  const [showNoTenantWarningModal, setShowNoTenantWarningModal] = useState(false);
  
  const [formData, setFormData] = useState(() => {
    // Inicializar con datos del usuario si existe, o vacío si es nuevo
    if (user) {
      return {
        email: user.email || '',
        username: user.username || '',
        full_name: user.full_name || '',
        phone: user.phone || '',
        nationality_code: user.nationality_code || 'PE',
        tenant_id: user.tenant_id || null,
        subscription_plan: tenants.find((tenant) => tenant.id === user.tenant_id)?.subscription_plan || 'premium',
        role_ids: user.roles?.map(r => r.id) || [],
        password: '',
      };
    } else {
      return {
        email: '',
        username: '',
        full_name: '',
        phone: '',
        nationality_code: 'PE',
        tenant_id: null,
        subscription_plan: 'premium',
        role_ids: [],
        password: '',
      };
    }
  });

  const selectedRoles = useMemo(
    () => (availableRoles || []).filter((r) => formData.role_ids.includes(r.id)),
    [availableRoles, formData.role_ids]
  );

  const isCreatingProvider = selectedRoles.some((r) => r.name === 'provider');

  // Resetear formulario solo cuando cambia el usuario (no en cada render)
  useEffect(() => {
    // Solo ejecutar si el usuario realmente cambió (comparación por referencia)
    if (previousUserRef.current !== user) {
      previousUserRef.current = user;
      // Resetear el flag de auto-selección cuando cambia el usuario
      autoSelectedRoleRef.current = false;
      
      if (!user) {
        // Nuevo usuario - resetear formulario
        // Si solo hay una opción de rol disponible, seleccionarla automáticamente
        const autoSelectedRoleIds = allowedRoles.length === 1 ? [allowedRoles[0].id] : [];
        if (autoSelectedRoleIds.length > 0) {
          autoSelectedRoleRef.current = true;
        }
        
        setFormData({
          email: '',
          username: '',
          full_name: '',
          phone: '',
          nationality_code: 'PE',
          tenant_id: null,
          subscription_plan: 'premium',
          role_ids: autoSelectedRoleIds,
          password: '',
        });
      } else {
        // Editar usuario - cargar datos
        setFormData({
          email: user.email || '',
          username: user.username || '',
          full_name: user.full_name || '',
          phone: user.phone || '',
          nationality_code: user.nationality_code || 'PE',
          tenant_id: user.tenant_id || null,
          subscription_plan: tenants.find((tenant) => tenant.id === user.tenant_id)?.subscription_plan || 'premium',
          role_ids: user.roles?.map(r => r.id) || [],
          password: '',
        });
      }
    }
    // Solo dependemos de user, no de allowedRoles para evitar reseteos constantes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Auto-seleccionar rol único cuando allowedRoles esté disponible y sea nuevo usuario
  // Solo se ejecuta una vez cuando allowedRoles se carga y aún no se ha auto-seleccionado
  useEffect(() => {
    // Solo para nuevos usuarios (user === null), cuando hay un solo rol disponible,
    // cuando no hay roles seleccionados, y cuando aún no se ha auto-seleccionado
    if (!user && allowedRoles.length === 1 && formData.role_ids.length === 0 && !autoSelectedRoleRef.current) {
      setFormData(prev => ({
        ...prev,
        role_ids: [allowedRoles[0].id]
      }));
      autoSelectedRoleRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedRoles.length, user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      const roleId = parseInt(value);
      setFormData((prev) => {
        const nextRoleIds = checked
          ? [...prev.role_ids, roleId]
          : prev.role_ids.filter(id => id !== roleId);
        const selectedRole = availableRoles?.find((r) => r.id === roleId);
        const clearingProvider = selectedRole?.name === 'provider' && checked;
        return {
          ...prev,
          role_ids: nextRoleIds,
          tenant_id: clearingProvider ? null : prev.tenant_id,
        };
      });
    } else {
      // Si es campo de teléfono, aplicar validación
      if (name === 'phone') {
        handlePhoneInput(e);
        setFormData((prev) => ({ ...prev, [name]: e.target.value }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    }
  };

  const handleTenantChange = (e) => {
    const value = e.target.value;
    const tenantId = value ? parseInt(value, 10) : null;
    const tenant = tenants.find((item) => item.id === tenantId);
    setFormData((prev) => ({
      ...prev,
      tenant_id: tenantId,
      subscription_plan: tenant?.subscription_plan || 'premium',
    }));
  };

  // Función para verificar si el formulario tiene datos
  const hasFormData = () => {
    return !!(
      formData.full_name ||
      formData.email ||
      formData.username ||
      formData.phone ||
      formData.tenant_id ||
      formData.role_ids.length > 0 ||
      formData.password
    );
  };

  // Función mejorada para manejar el cierre del modal con confirmación
  const handleCancel = () => {
    if (hasFormData() && !user) {
      // Solo para nuevos usuarios con datos - mostrar modal de confirmación
      setShowCloseConfirmModal(true);
    } else {
      onCancel();
    }
  };

  const confirmClose = () => {
    setShowCloseConfirmModal(false);
    onCancel();
  };

  const cancelClose = () => {
    setShowCloseConfirmModal(false);
  };

  // Función auxiliar para preparar y enviar los datos
  const prepareAndSubmit = () => {
    // Preparar datos para enviar
    const submitData = { ...formData };
    
    // Limpiar número de teléfono antes de enviar
    if (submitData.phone) {
      submitData.phone = cleanPhoneNumber(submitData.phone) || null;
    }

    const rolesForSubmit = (availableRoles || []).filter((r) => submitData.role_ids.includes(r.id));
    if (rolesForSubmit.some((r) => r.name === 'provider')) {
      submitData.tenant_id = null;
      delete submitData.subscription_plan;
    }
    
    // Si no es superadmin, asignar automáticamente el tenant del usuario actual
    if (!currentUser?.is_superuser && !currentUser?.roles?.some(r => r.name === 'superadmin')) {
      submitData.tenant_id = currentUser?.tenant_id || null;
    } else {
      // Si es superadmin, asegurarse de que tenant_id se envíe correctamente (puede ser null)
      // Convertir string vacío a null
      if (submitData.tenant_id === '' || submitData.tenant_id === undefined) {
        submitData.tenant_id = null;
      }
    }
    
    // Si es edición y no hay contraseña, no enviarla
    if (user && !submitData.password) {
      delete submitData.password;
    }
    
    onSubmit(submitData);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validar campos requeridos
    if (!formData.full_name || !formData.email || !formData.username) {
      toast.error('Por favor, completa todos los campos requeridos');
      return;
    }
    
    // Validar que tenga al menos un rol
    if (formData.role_ids.length === 0) {
      toast.error('Debes seleccionar al menos un rol');
      return;
    }
    
    // Validar contraseña para nuevos usuarios
    if (!user && !formData.password) {
      toast.error('La contraseña es requerida para nuevos usuarios');
      return;
    }
    
    // Validar longitud mínima de contraseña
    if (formData.password && formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    // Validar que los roles seleccionados estén permitidos
    const selectedRoles = availableRoles?.filter(r => formData.role_ids.includes(r.id)) || [];
    const invalidRoles = selectedRoles.filter(r => !allowedRoles.find(ar => ar.id === r.id));
    
    if (invalidRoles.length > 0) {
      toast.error(`No tienes permisos para asignar los roles: ${invalidRoles.map(r => ROLE_LABELS[r.name] || r.name).join(', ')}`);
      return;
    }
    
    // Preparar datos para validación de tenant
    const submitData = { ...formData };
    
    // Si no es superadmin, asignar automáticamente el tenant del usuario actual
    if (!currentUser?.is_superuser && !currentUser?.roles?.some(r => r.name === 'superadmin')) {
      submitData.tenant_id = currentUser?.tenant_id || null;
    } else {
      // Si es superadmin, asegurarse de que tenant_id se envíe correctamente (puede ser null)
      // Convertir string vacío a null
      if (submitData.tenant_id === '' || submitData.tenant_id === undefined) {
        submitData.tenant_id = null;
      }
      
      // Validar que usuarios no-superuser tengan organización asignada
      const selectedRoles = availableRoles?.filter(r => formData.role_ids.includes(r.id)) || [];
      const isCreatingSuperuser = selectedRoles.some(r => r.name === 'superadmin');

      if (!submitData.tenant_id && !isCreatingSuperuser && !isCreatingProvider && !user) {
        // Usuario nuevo sin organización y no es superuser - mostrar modal de advertencia
        setShowNoTenantWarningModal(true);
        return; // Pausar el envío hasta que el usuario confirme
      }
    }
    
    // Si pasó todas las validaciones, enviar
    prepareAndSubmit();
  };

  return (
    <form onSubmit={handleSubmit} className="user-form" autoComplete="off">
      <div className="form-group">
        <label htmlFor="full_name">Nombre Completo *</label>
        <input
          type="text"
          id="full_name"
          name="full_name"
          value={formData.full_name}
          onChange={handleChange}
          required
          disabled={isLoading}
        />
      </div>

      <div className="form-group">
        <label htmlFor="email">Email *</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          disabled={isLoading}
        />
      </div>

      <div className="form-group">
        <label htmlFor="phone">Teléfono</label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          disabled={isLoading}
          placeholder="+51 900 000 000"
        />
      </div>

      <div className="form-group">
        <label htmlFor="nationality_code">Nacionalidad</label>
        <NationalitySelect
          id="nationality_code"
          value={formData.nationality_code}
          disabled={isLoading}
          onChange={(code) => handleChange({ target: { name: 'nationality_code', value: code } })}
        />
      </div>

      <div className="form-group">
        <label>Roles *</label>
        {allowedRoles.length === 0 ? (
          <div style={{ padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
            <small style={{ color: '#856404' }}>
              No tienes permisos para crear usuarios con ningún rol disponible.
            </small>
          </div>
        ) : (
          <>
            <div className="roles-checkboxes">
              {allowedRoles.map(role => (
                <label key={role.id} className="checkbox-label">
                  <input
                    type="checkbox"
                    value={role.id}
                    checked={formData.role_ids.includes(role.id)}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                  <span>{ROLE_LABELS[role.name] || role.name}</span>
                </label>
              ))}
            </div>
            {formData.role_ids.length === 0 && (
              <small style={{ color: '#d32f2f' }}>Selecciona al menos un rol</small>
            )}
            {allowedRoles.length < (availableRoles?.length || 0) && (
              <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                Solo puedes crear usuarios con los roles permitidos para tu nivel de acceso.
              </small>
            )}
          </>
        )}
      </div>

      {!isCreatingProvider && (
        <div className="form-group">
          <label htmlFor="tenant_id">Organización</label>
          <select
            id="tenant_id"
            name="tenant_id"
            value={formData.tenant_id || ''}
            onChange={handleTenantChange}
            disabled={isLoading || (!currentUser?.is_superuser && !currentUser?.roles?.some(r => r.name === 'superadmin'))}
          >
            {currentUser?.is_superuser || currentUser?.roles?.some(r => r.name === 'superadmin') ? (
              <>
                <option value="">Sin organización (Superusuario)</option>
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name} ({tenant.code})
                  </option>
                ))}
              </>
            ) : (
              <>
                <option value={currentUser?.tenant_id || ''}>
                  {tenants?.find(t => t.id === currentUser?.tenant_id)?.name || 'Mi organización'}
                </option>
              </>
            )}
          </select>
          {!currentUser?.is_superuser && !currentUser?.roles?.some(r => r.name === 'superadmin') && (
            <small>Los usuarios se crearán en tu organización</small>
          )}
          {(currentUser?.is_superuser || currentUser?.roles?.some(r => r.name === 'superadmin')) &&
           !formData.tenant_id &&
           !user &&
           formData.role_ids.length > 0 &&
           !formData.role_ids.some(rid => {
             const role = availableRoles?.find(r => r.id === rid);
             return role?.name === 'superadmin' || role?.name === 'provider';
           }) && (
            <div style={{
              marginTop: '8px',
              padding: '12px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px'
            }}>
              <span style={{ fontSize: '18px' }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <strong style={{ color: '#856404', display: 'block', marginBottom: '4px' }}>
                  Usuario sin organización asignada
                </strong>
                <small style={{ color: '#856404', display: 'block', lineHeight: '1.5' }}>
                  El usuario <strong>NO podrá iniciar sesión</strong> hasta que se le asigne una organización.
                  Puedes asignarla ahora o después editando el usuario.
                </small>
              </div>
            </div>
          )}
          {user && !formData.tenant_id && !user.is_superuser && !isCreatingProvider && (
            <div style={{
              marginTop: '8px',
              padding: '12px',
              backgroundColor: '#f8d7da',
              border: '1px solid #dc3545',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px'
            }}>
              <span style={{ fontSize: '18px' }}>❌</span>
              <div style={{ flex: 1 }}>
                <strong style={{ color: '#721c24', display: 'block', marginBottom: '4px' }}>
                  Usuario sin organización
                </strong>
                <small style={{ color: '#721c24', display: 'block', lineHeight: '1.5' }}>
                  Este usuario <strong>NO puede iniciar sesión</strong> porque no tiene una organización asignada.
                  Por favor, asigna una organización para que pueda acceder al sistema.
                </small>
              </div>
            </div>
          )}
        </div>
      )}

      {(currentUser?.is_superuser || currentUser?.roles?.some((role) => role.name === 'superadmin')) &&
        formData.tenant_id &&
        !isCreatingProvider && (
        <div className="form-group">
          <label htmlFor="subscription_plan">Plan de suscripción</label>
          <select
            id="subscription_plan"
            name="subscription_plan"
            value={formData.subscription_plan || 'premium'}
            onChange={handleChange}
            disabled={isLoading}
          >
            <option value="free">free</option>
            <option value="premium">premium</option>
          </select>
          <small>La membresía define las funciones disponibles para cada organización.</small>
        </div>
      )}

      {isCreatingProvider && (
        <p className="form-hint" style={{ color: '#666', fontSize: '0.9rem', margin: '0 0 1rem' }}>
          Los proveedores publicitarios no requieren organización; acceden al portal de anuncios directamente.
        </p>
      )}

      <div className="form-section-divider" style={{ marginTop: '8px', paddingTop: '16px', borderTop: '2px solid #e0e0e0' }}>
        <h4 style={{ margin: '0 0 12px 0', color: '#2c3e50', fontSize: '15px', fontWeight: 600 }}>Credenciales de Acceso</h4>
      </div>

      <div className="form-group">
        <label htmlFor="username">Usuario *</label>
        <input
          type="text"
          id="username"
          name="username"
          value={formData.username}
          onChange={handleChange}
          required
          disabled={isLoading || !!user}
          autoComplete={user ? "username" : "off"}
        />
        {user && <small>El usuario no se puede modificar</small>}
      </div>

      <div className="form-group">
        <label htmlFor="password">
          {user ? 'Nueva Contraseña (dejar vacío para mantener la actual)' : 'Contraseña *'}
        </label>
        <div className="password-input-wrapper">
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required={!user}
            disabled={isLoading}
            minLength={6}
            autoComplete={user ? "new-password" : "new-password"}
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            disabled={isLoading}
          >
            {showPassword ? '👁️' : '👁️‍🗨️'}
          </button>
        </div>
        {user && <small>Dejar vacío para mantener la contraseña actual</small>}
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={isLoading}>
          Cancelar
        </button>
        <button type="submit" className="btn btn-primary" disabled={isLoading || formData.role_ids.length === 0}>
          {isLoading ? 'Guardando...' : user ? 'Actualizar' : 'Crear'}
        </button>
      </div>

      {/* Modal de confirmación para cerrar formulario */}
      <Modal
        isOpen={showCloseConfirmModal}
        onClose={cancelClose}
        title="Confirmar Cierre"
        closeOnOverlayClick={true}
      >
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p style={{ fontSize: '1.1em', marginBottom: '20px' }}>
            ¿Estás seguro de que deseas cerrar? Se perderán los datos ingresados.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
            <button className="btn btn-secondary" onClick={cancelClose}>
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={confirmClose} style={{ backgroundColor: '#e74c3c' }}>
              Cerrar
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de advertencia para crear usuario sin organización */}
      <Modal
        isOpen={showNoTenantWarningModal}
        onClose={() => setShowNoTenantWarningModal(false)}
        title="⚠️ Advertencia"
        closeOnOverlayClick={false}
      >
        <div style={{ padding: '20px' }}>
          <div style={{ 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffc107', 
            borderRadius: '6px', 
            padding: '15px', 
            marginBottom: '20px' 
          }}>
            <p style={{ margin: '0 0 10px 0', fontWeight: '600', color: '#856404' }}>
              El usuario se creará SIN organización asignada.
            </p>
            <p style={{ margin: '0', color: '#856404' }}>
              El usuario NO podrá iniciar sesión hasta que se le asigne una organización.
            </p>
          </div>
          <p style={{ marginBottom: '20px', color: '#555' }}>
            ¿Deseas continuar? Puedes asignar la organización después editando el usuario.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowNoTenantWarningModal(false)}
            >
              Cancelar
            </button>
            <button 
              className="btn btn-primary" 
              onClick={() => {
                setShowNoTenantWarningModal(false);
                // Continuar con el envío del formulario usando la función auxiliar
                prepareAndSubmit();
              }}
              style={{ backgroundColor: '#f39c12' }}
            >
              Continuar
            </button>
          </div>
        </div>
      </Modal>
    </form>
  );
};

export default Users;

